import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SERVICES, CLIENTS } from "@/data/site";
import { INSIGHTS } from "@/data/insights";
import { LOCATIONS, SERVICE_AREAS, getLocation } from "@/data/areas";

const BASE_URL = "https://clickworldonline.com";

const SEO_MAP = {
  "/": "Click World Online is a digital marketing agency in Mansa, Punjab — strategy, SEO, Google & Meta ads, social media, lead generation, websites and 360° virtual tours since 2017.",
  "/about": "Founded on 30 April 2017 in Mansa, Punjab, Click World Online is a digital marketing agency built on strategy, creativity and honest work. Meet founder Akshay Jindal.",
  "/services": "Explore digital marketing services from Click World Online — SEO, Google Ads, Meta advertising, social media, lead generation, website design & development, branding and 360° virtual tours.",
  "/portfolio": "See the brands Click World Online has helped click — 800+ clients served since 2017 across digital marketing, social media, websites and 360° virtual tours.",
  "/case-studies": "How Click World Online documents client work — challenge, strategy, execution, outcome, with verified results only.",
  "/360-experiences": "Explore 400+ published 360° virtual tours and Google Street View experiences captured by Click World Online for businesses across India.",
  "/contact": "Contact Click World Online in Mansa, Punjab. Call +91 73079 58742, email info@clickworldonline.com or send an enquiry to grow your brand online.",
  "/insights": "Practical digital marketing thinking from Click World Online — SEO, social media, advertising and growth insights for businesses in Punjab and beyond.",
  "/faq": "Answers to common questions about Click World Online's website development, SEO, digital marketing, Google Business Profile, lead generation and 360° virtual tour services in Mansa, Punjab.",
  "/careers": "Careers at Click World Online — join our digital marketing team in Mansa, Punjab. Open roles in SEO, social media, content writing, 360° photography, web development and business development.",
  "/service-areas": "Click World Online is based in Mansa, India and serves businesses across India and international markets — including Canada, the USA, the UK and Australia — with selected digital services delivered remotely worldwide.",
  "/privacy-policy": "How Click World Online collects, uses and protects your information when you contact us through this website.",
  "/terms-and-conditions": "Terms and conditions for using the Click World Online website and engaging our digital marketing services.",
  "/disclaimer": "Disclaimer for the Click World Online website — honest information, no guaranteed marketing outcomes.",
};

const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let desc = SEO_MAP[pathname];
    let crumbs = null;
    let serviceSchema = null;

    const sm = pathname.match(/^\/services\/(.+)$/);
    const pm = pathname.match(/^\/portfolio\/(.+)$/);
    const im = pathname.match(/^\/insights\/(.+)$/);
    const lm = pathname.match(/^\/locations\/(.+)$/);
    const cm = pathname.match(/^\/([a-z0-9-]+)\/([a-z0-9-]+)$/);

    const crumbList = (items) =>
      items.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: BASE_URL + c.path,
      }));

    if (sm) {
      const s = SERVICES.find((x) => x.slug === sm[1]);
      if (s) {
        desc = `${s.title} by Click World Online — ${s.short} Serving businesses in Mansa, Punjab and beyond since 2017.`;
        crumbs = crumbList([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: s.title, path: pathname },
        ]);
        serviceSchema = {
          "@context": "https://schema.org",
          "@type": "Service",
          name: s.title,
          description: s.overview,
          provider: { "@id": "https://clickworldonline.com/#organization" },
          areaServed: ["Mansa", "Punjab", "India"],
        };
      }
    } else if (pm) {
      const c = CLIENTS.find((x) => x.slug === pm[1]);
      if (c) {
        desc = `${c.name} — ${c.category} work by Click World Online, digital marketing agency in Mansa, Punjab.`;
        crumbs = crumbList([
          { name: "Home", path: "/" },
          { name: "Our Work", path: "/portfolio" },
          { name: c.name, path: pathname },
        ]);
      }
    } else if (im) {
      const a = INSIGHTS.find((x) => x.slug === im[1]);
      if (a) {
        desc = a.excerpt;
        crumbs = crumbList([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: a.title, path: pathname },
        ]);
      }
    } else if (pathname === "/locations") {
      desc = "Areas served by Click World Online — Mansa (HQ), Bathinda, Chandigarh, Mohali and businesses across Punjab. Digital marketing, SEO, websites and 360° tours.";
    } else if (lm) {
      const l = getLocation(lm[1]);
      if (l) {
        desc = `Digital marketing, SEO, website development and 360° virtual tours in ${l.name} — by Click World Online, Mansa-based agency serving Punjab since 2017.`;
        crumbs = crumbList([
          { name: "Home", path: "/" },
          { name: "Areas We Serve", path: "/locations" },
          { name: l.name, path: pathname },
        ]);
      }
    } else if (cm) {
      const a = SERVICE_AREAS.find((x) => x.service === cm[1] && x.city === cm[2]);
      if (a) {
        const l = getLocation(a.city);
        desc = `${a.keyword} — ${a.intro.slice(0, 130)}`;
        crumbs = crumbList([
          { name: "Home", path: "/" },
          { name: l.name, path: `/locations/${l.slug}` },
          { name: a.keyword, path: pathname },
        ]);
      }
    }

    const meta = document.querySelector('meta[name="description"]');
    if (meta && desc) meta.setAttribute("content", desc);

    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", BASE_URL + pathname);

    let tag = document.getElementById("breadcrumb-jsonld");
    if (crumbs) {
      if (!tag) {
        tag = document.createElement("script");
        tag.id = "breadcrumb-jsonld";
        tag.type = "application/ld+json";
        document.head.appendChild(tag);
      }
      tag.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs,
      });
    } else if (tag) {
      tag.remove();
    }

    let stag = document.getElementById("service-jsonld");
    if (serviceSchema) {
      if (!stag) {
        stag = document.createElement("script");
        stag.id = "service-jsonld";
        stag.type = "application/ld+json";
        document.head.appendChild(stag);
      }
      stag.textContent = JSON.stringify(serviceSchema);
    } else if (stag) {
      stag.remove();
    }
  }, [pathname]);

  return null;
};

export default SeoManager;
